import { useGetAPI, usePostAPI, usePatchAPI, useDeleteAPI } from './useAPI';
import { useRecoilValue } from 'recoil';
import { authenticatedUser, SID, XCustomerID } from '@state';
import { useMultiRateRoute, useMultiRateStore } from '@hooks';
import {
	AJAXError,
	TRateCard,
	TBookShipmentShipAPI,
	APICallProps,
	TShipmentEOS,
	IShipperQuoteSession,
	TShipmentOrder,
	TInvoiceBilling,
	TInvoiceDetails,
	TShipperDataExtract,
	TShipperPrepDataExtract,
	TShipperSettings,
	TShipmentFullBooking,
	TShipmentPartialBooking,
	TShipmentToday,
	ITruckLocationResponse,
	IQuotesResponse,
	ILinehaulTotalsResponse,
	ILinehaulDataResponse,
	TShipmentNote,
	TShipmentNoteCategory,
	Link,
	TBulkUploadItem,
	TMeta,
	TAdminCompanyDetails,
	TAdminTeamResponse,
	TCostCentres,
	TShipmentNoteSubCategory,
	TBulkAnalyticsResponse,
	TAdminAvailableTeamResponse,
	TDataExtractTaskHistories,
	TLicence,
	TShipperBillingSettings,
	TMasterShipmentMultiShipment,
	TCompanyDocument,
	TDifotPerformanceSummary,
	TCarbonLanesSummary,
	TCarbonTrendSummary,
	TDifotMissedShipmentsPage,
	TShipmentMissesGroup,
	TShipperLanePerformanceSummary,
	TPodPerformanceSummary,
} from '@types';

/* 	SHIPPER */
export const useGetShipperOrders = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/orders' });

	return {
		loadingOrders: isLoading,
		orders: data as { rows: TShipmentEOS[] },
		getOrdersError: error as AJAXError,
		clearGetOrdersError: clearError,
		callGetOrders: callAPI as (props?: APICallProps) => Promise<{
			data: { rows: TShipmentEOS[] } | null;
			error: AJAXError | null
		}>
	}
}
export const useGetShipperOrders2 = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/orders', });

	return {
		loadingOrders: isLoading,
		orders: data as { rows: TShipmentEOS[] },
		getOrdersError: error as AJAXError,
		clearGetOrdersError: clearError,
		callGetOrders2: callAPI as (props?: APICallProps) => Promise<{
			data: { rows: TShipmentEOS[] } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetEditShipment = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/shipment/{1}', headers });

	return {
		loadingEditShipment: isLoading,
		editShipment: data as any,
		editShipmentErrors: error,
		callGetEditShipment: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const usePatchEditShipment = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/shipper/full-bookings/{1}', headers });

	return {
		isLoadingPatchShipment: isLoading,
		patchShipment: data as { data: TShipmentOrder },
		patchShipmentErrors: error,
		callPatchShipment: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TShipmentOrder };
			error: AJAXError | null
		}>
	}
}

export const useGetFrequentAddresses = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/address/frequentlist', headers });

	return {
		loadingFrequentAddresses: isLoading,
		frequentAddresses: data as any,
		frequentAddressesErrors: error,
		callGetFrequentAddresses: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const usePatchShipmentAddress = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/shipper/shipment-address/{1}', headers });

	return {
		isLoadingPatchShipmentAddress: isLoading,
		patchShipmentAddress: data as any,
		patchShipmentAddressErrors: error,
		callPatchShipmentAddress: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const usePatchShipmentFrequentAddress = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/shipper/shipment-frequestn-address/{1}', headers });

	return {
		isLoadingPatchShipmentFrequestAddress: isLoading,
		patchShipmentFrequestAddress: data as any,
		patchShipmentFrequestAddressErrors: error,
		callPatchShipmentFrequestAddress: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperOrder = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = sID ? { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/order/{1}', headers: headers });

	return {
		loadingOrder: isLoading,
		order: data as TShipmentOrder,
		getOrderError: error as AJAXError,
		clearGetOrderError: clearError,
		clearGetOrderData: clearData,
		callGetOrder: callAPI as (props?: APICallProps) => Promise<{
			data: TShipmentOrder | null;
			error: AJAXError | null
		}>
	}
}

/**
 * Fetch shipment order by external ID (human-readable URL-friendly ID).
 * Preferred over useGetShipperOrder for external-facing URLs.
 */
export const useGetShipperOrderByExternalId = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = sID ? { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/order-by-external-id/{1}', headers: headers });

	return {
		loadingOrder: isLoading,
		order: data as TShipmentOrder,
		getOrderError: error as AJAXError,
		clearGetOrderError: clearError,
		clearGetOrderData: clearData,
		callGetOrder: callAPI as (props?: APICallProps) => Promise<{
			data: TShipmentOrder | null;
			error: AJAXError | null
		}>
	}
}

export const useGetQuoteSession = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/quote-session', headers: headers });

	return {
		loadingQuoteSession: isLoading,
		quoteSession: data as { data: { shipper_quote_session: IShipperQuoteSession } },
		getQuoteSessionError: error as AJAXError,
		clearQuoteSessionError: clearError,
		callGetQuoteSession: callAPI as (props?: APICallProps) => Promise<{
			data: { shipper_quote_session: IShipperQuoteSession } | null;
			error: AJAXError | null
		}>
	}
}

export const usePostLinehaulShipmentTotals = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/linehaul/shipment/totals' });

	return {
		loadingLinehaulData: isLoading,
		callLinehaulTotals: callAPI as (props?: APICallProps) => Promise<{
			data: { data: ILinehaulTotalsResponse } | null;
			error: AJAXError | null
		}>
	}
}

export const usePostLinehaulShipmentData = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/linehaul/shipment/data' });

	return {
		loadingLinehaulData: isLoading,
		callLinehaulData: callAPI as (props?: APICallProps) => Promise<{
			data: { data: ILinehaulDataResponse[] } | null;
			error: AJAXError | null
		}>
	}
}

export const usePostLinkLinehaulShipments = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/linehaul/shipment/link' });

	return {
		loadingLinehaulData: isLoading,
		linkLinehaulShipments: callAPI as (props?: APICallProps) => Promise<{
			data: { data: [] } | null;
			error: AJAXError | null
		}>
	}
}


export const useGetPartialShipment = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipment/{1}',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingPartialShipment: isLoading,
		partialShipment: data?.data as TBookShipmentShipAPI,
		getPartialShipmentError: error as AJAXError,
		clearPartialShipmentError: clearError,
		callGetPartialShipment: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TBookShipmentShipAPI } | null;
			error: AJAXError | null
		}>
	}
}

export const usePostBookShipment = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;
	const multiRateStore = useMultiRateStore();

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({
		endpoint: '/api/partial-shipment',
		domain: 'ShipmentAPI',
		headers
	});

	const callBookShipment = (props?: APICallProps) => callAPI(multiRateStore(props)) as Promise<{
		data: { data: TBookShipmentShipAPI } | null;
		error: AJAXError | null
	}>;

	return {
		loadingBookShipment: isLoading,
		bookedShipment: data?.data as TBookShipmentShipAPI,
		bookShipmentError: error as AJAXError,
		clearBookShipmentError: clearError,
		callBookShipment
	}
}

export const useSaveShipmentProgress = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePatchAPI({
		endpoint: '/api/partial-shipment/{1}',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingSaveShipment: isLoading,
		savedShipment: data?.data as TBookShipmentShipAPI,
		savedShipmentError: error as AJAXError,
		clearSavedShipmentError: clearError,
		clearSavedShipmentData: clearData,
		callSaveShipment: callAPI as (props?: APICallProps) => Promise<{
			data: TBookShipmentShipAPI | null;
			error: AJAXError | null
		}>
	}
}

export const useFinalizeShipment = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePostAPI({
		endpoint: '/api/partial-shipment/{1}/finalize',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingFinalizeShipment: isLoading,
		finalizedShipment: data?.data as TBookShipmentShipAPI,
		finalizedShipmentError: error as AJAXError,
		clearFinalizeShipmentError: clearError,
		clearFinalizeShipmentData: clearData,
		callFinalizeShipment: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TBookShipmentShipAPI } | null;
			error: AJAXError | null
		}>
	}
}

export const useCreateQuoteFromBooking = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID && sID > 0 ? sID : authUser?.shipper?.id;
	const headers = shipperToken ? { 'X-Shipper-Token': `${shipperToken}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper/booking/quote',
		headers
	});

	return {
		loadingCreateQuote: isLoading,
		createdQuote: data as any,
		createdQuoteError: error as AJAXError,
		clearCreatedQuoteError: clearError,
		clearCreatedQuoteData: clearData,
		callCreateQuoteFromBooking: callAPI as (props?: APICallProps) => Promise<{
			data: any | null;
			error: AJAXError | null
		}>
	}
}

type TMachshipQuotePayload = {
	id?: number;
	consignmentNumber?: string;
};

type TMachshipQuoteResponse = {
	object?: TMachshipQuotePayload;
};

export const useCreateMachshipQuoteFromBooking = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID && sID > 0 ? sID : authUser?.shipper?.id;
	const headers = shipperToken ? { 'X-Shipper-Token': `${shipperToken}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper/booking/machship/quote',
		headers
	});

	return {
		loadingCreateMachshipQuote: isLoading,
		createdMachshipQuote: data as TMachshipQuoteResponse,
		createdMachshipQuoteError: error as AJAXError,
		clearCreatedMachshipQuoteError: clearError,
		clearCreatedMachshipQuoteData: clearData,
		callCreateMachshipQuoteFromBooking: callAPI as (props?: APICallProps) => Promise<{
			data: TMachshipQuoteResponse | null;
			error: AJAXError | null
		}>
	}
}

export const useRemoveDocument = () => {
	const {
		isLoading,
		error,
		data,
		clearError,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/documents/{1}' });

	const removeDocument = async (documentId: string | number) => {
		await callAPI({ routes: [String(documentId)] });
	};

	return {
		deleting: isLoading,
		error: error,
		data,
		clearError: clearError,
		removeDocument
	};
};

export const useRemoveShipmentDoc = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/stop-document/{1}',
		domain: 'ShipmentAPI'
	});

	return {
		loadingRemoveDoc: isLoading,
		removedDoc: data?.data as { success: boolean },
		removedDocError: error as AJAXError,
		clearRemovedDocError: clearError,
		clearRemovedDocData: clearData,
		callRemoveDoc: callAPI as (props?: APICallProps) => Promise<{
			data: { success: boolean } | null;
			error: AJAXError | null
		}>
	}
}

export const useDeleteShipmentDoc = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID && sID > 0 ? sID : authUser?.shipper?.id;
	const headers = shipperToken ? { 'X-Shipper-Token': `${shipperToken}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/shipper/shipment-address-document/{1}',
		headers
	});

	return {
		loadingDeleteDoc: isLoading,
		deleteDoc: data?.data as unknown, // 204 response with no content
		deleteDocError: error as AJAXError,
		clearDeleteDocError: clearError,
		clearDeleteDocData: clearData,
		callDeleteDoc: callAPI as (props?: APICallProps) => Promise<{
			data: unknown; // 204 response with no content;
			error: AJAXError | null
		}>
	}
}

export const useStoreShipmentAddressDoc = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID && sID > 0 ? sID : authUser?.shipper?.id;
	const headers = shipperToken ? { 'X-Shipper-Token': `${shipperToken}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper/shipment-address-document',
		headers
	});

	return {
		loadingStoreDoc: isLoading,
		storeDocError: error as AJAXError,
		storeDoc: data?.data,
		callStoreDoc: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const usePostBookingPrices = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;
	const multiRateRoute = useMultiRateRoute();

	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePostAPI({
		endpoint: '/api/partial-shipment/prices',
		domain: 'ShipmentAPI',
		headers
	});

	const callPostBookingPrices = (props?: APICallProps) => callAPI(multiRateRoute('/shipmentapi/api/v2/partial-shipment/prices', props)) as Promise<{
		data: { data: TRateCard[] } | null;
		error: AJAXError | null
	}>;

	return {
		loadingBookingPrices: isLoading,
		bookingPrices: data?.data as TRateCard[],
		bookingPricesError: error as AJAXError,
		clearBookingPricesError: clearError,
		clearBookingPricesData: clearData,
		callPostBookingPrices
	}
}

export const useGetShipmentQuote = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID && sID > 0 ? sID : authUser?.shipper?.id;
	const headers = shipperToken ? { 'X-Shipper-Token': `${shipperToken}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/booking/quote/{1}', headers });

	return {
		loadingShipmentQuote: isLoading,
		shipmentQuote: data as { quote: TBookShipmentShipAPI },
		shipmentQuoteError: error as AJAXError,
		clearShipmentQuoteError: clearError,
		callGetShipmentQuote: callAPI as (props?: APICallProps) => Promise<{
			data: { quote: TBookShipmentShipAPI };
			error: AJAXError | null
		}>
	}
}

export const useGetShipmentIdFromRef = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/idfromref/{1}', headers });

	return {
		loadingIdFromRef: isLoading,
		idFromRef: data as { id: number, external_id: string, reference: string },
		idFromRefError: error as AJAXError,
		clearIdFromRefError: clearError,
		callGetIdFromRef: callAPI as (props?: APICallProps) => Promise<{
			data: { id: number, external_id: string, reference: string } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetIsAutoAllocated = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/autoallocated/{1}', headers });

	return {
		loadingIsAutoAllocated: isLoading,
		isAutoAllocated: data as { auto_allocated: boolean; carrier: string },
		isAutoAllocatedError: error as AJAXError,
		clearIsAutoAllocatedError: clearError,
		callGetIsAutoAllocated: callAPI as (props?: APICallProps) => Promise<{
			data: { auto_allocated: boolean; carrier: string; failure_reason?: string; };
			error: AJAXError | null
		}>
	}
}

// SHIPPER -> SHIPMENTS MAP

export const useGetShipmentsForDate = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID ?? authUser?.shipper?.id;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({
		domain: 'Inertia',
		endpoint: `/shipper/shipments-for-date?sid=${shipperToken}&date={1}`
	});

	return {
		loadingshipmentsForDate: isLoading,
		shipmentsForDate: data?.data as TShipmentToday[],
		getShipmentsForDateError: error as AJAXError,
		clearShipmentsForDateError: clearError,
		callGetShipmentsForDate: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TShipmentToday[]
			},
			error: AJAXError | null
		}>
	}
}

export const useGetTrucksLocation = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI,
	} = usePostAPI({
		endpoint: `${import.meta.env.VITE_MIX_NAVIGATION_API_URL}/query`,
		headers: { 'X-Api-Key': `${import.meta.env.VITE_MIX_NAVIGATION_API_KEY}` }
	});

	return {
		isTrucksLocationLoading: isLoading,
		trucksLocationData: data as ITruckLocationResponse[],
		trucksLocationError: error,
		clearTrucksLocationError: clearError,
		clearTrucksLocationData: clearData,
		callGetTrucksLocation: callAPI as (props?: APICallProps) => Promise<{
			data: ITruckLocationResponse[] | null
			error: AJAXError | null
		}>,
	};
};


/* 	ADMIN -> SHIPPER -> SHIPMENTS */
export const useGetShipperQuotes = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID && sID > 0 ? sID : authUser?.shipper?.id;
	const headers = shipperToken ? { 'X-Shipper-Token': `${shipperToken}` } : undefined;
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/quotes',
		headers
	});

	return {
		loadingShipperQuotes: isLoading,
		quotes: data as IQuotesResponse,
		getQuotesError: error,
		clearQuotesError: clearError,
		clearQuotesData: clearData,
		callGetShipperQuotes: callAPI as (props?: APICallProps) => Promise<{
			data: IQuotesResponse;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperPartialBookings = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/partial-shipment',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingPartialBookings: isLoading,
		partialBookings: data as any[],
		partialBookingsError: error,
		callGetShipperPartialBookings: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TShipmentPartialBooking[],
				links: any[],
				meta: {
					total: number
				}
			},
			error: AJAXError | null
		}>
	}
}

export const useGetShipperFullBookings = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/full-bookings',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingFullBookings: isLoading,
		fullBookings: data as any[],
		fullBookingsError: error,
		callGetShipperFullBookings: callAPI as (props?: APICallProps) => Promise<{
			data: {
				rows: TShipmentFullBooking[],
				total: number,
				currentPage: number,
				perPage: number,
			},
			error: AJAXError | null
		}>
	}
}

export const useDeletePartialShipment = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/partial-shipment/{1}',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingDeletePartial: isLoading,
		deletedPartial: data,
		getDeletePartialError: error,
		callDeletePartial: callAPI,
	}
}

export const useDeleteFullShipment = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/shipper/order/cancel/{1}',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		isLoading,
		deleteFullShipment: data,
		getDeleteFullShipmentError: error,
		callDeleteFullShipment: callAPI as (props?: APICallProps) => Promise<{
			data: any[],
			error: AJAXError | null
		}>
	}
}

export const useGetShipmentPods = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser)
	const {
		isLoading,
		data,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/order/{1}/download-verified-pods',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		isLoading,
		pods: data,
		callGetDownloadVerifiedPods: callAPI as (props?: APICallProps) => Promise<{
			data: any,
			error: AJAXError | null
		}>
	}
}

/* SHIPPER -> INVOICES */
export const useGetShipperInvoices = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/invoices',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingInvoices: isLoading,
		invoices: data as any,
		getInvoicesError: error,
		callGetShipperInvoices: callAPI as (props?: APICallProps) => Promise<{
			data: TInvoiceBilling;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperInvoice = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/invoices/{1}/detail',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingInvoice: isLoading,
		invoice: data as TInvoiceDetails,
		getInvoicesError: error,
		callGetShipperInvoice: callAPI as (props?: APICallProps) => Promise<{
			data: TInvoiceDetails;
			error: AJAXError | null
		}>
	}
}

export const useDownloadShipperInvoice = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/invoices/{1}/download',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingDownloadInvoice: isLoading,
		shipperInvoice: data as any,
		shipperInvoiceError: error,
		callDownloadShipperInvoice: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

/* 	Shipper -> Analytics */
export const useGetDifotPerformanceSummary = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/difot/performance-summary', headers });

	return {
		loadingDifotSummary: isLoading,
		difotSummary: data as TDifotPerformanceSummary,
		getDifotSummaryError: error as AJAXError,
		clearDifotSummaryError: clearError,
		clearDifotSummaryData: clearData,
		callGetDifotPerformanceSummary: callAPI as (props?: APICallProps) => Promise<{
			data: TDifotPerformanceSummary;
			error: AJAXError | null
		}>
	}
}

export const useGetDifotMissedShipments = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/difot/missed-shipments', headers });

	return {
		loadingDifotMissedShipments: isLoading,
		difotMissedShipments: data as TDifotMissedShipmentsPage,
		getDifotMissedShipmentsError: error as AJAXError,
		clearDifotMissedShipmentsError: clearError,
		clearDifotMissedShipmentsData: clearData,
		callGetDifotMissedShipments: callAPI as (props?: APICallProps) => Promise<{
			data: TDifotMissedShipmentsPage;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperShipmentMisses = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/difot/shipments/{1}/misses', headers });

	return {
		loadingGetMissRecords: isLoading,
		callGetMissRecords: callAPI as (props?: APICallProps) => Promise<{
			data: { data: { sifot: TShipmentMissesGroup; difot: TShipmentMissesGroup } };
			error: AJAXError | null;
		}>
	};
};

export const useGetDifotShipperOptions = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/difot/shipper-options', headers });

	return {
		loadingDifotShipperOpts: isLoading,
		difotShipperOpts: data as { data: { label: string; value: number }[] },
		getDifotShipperOptsError: error as AJAXError,
		clearDifotShipperOpts: clearData,
		clearDifotShipperOptsError: clearError,
		callGetDifotShipperOpts: callAPI as (props?: APICallProps) => Promise<{
			data: { data: { label: string; value: number }[] };
			error: AJAXError | null
		}>
	}
}

export const useGetShipperLanePerformance = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/lanes/performance', headers });

	return {
		loadingLanePerformance: isLoading,
		lanePerformance: data as TShipperLanePerformanceSummary,
		getShipperLanePerformanceError: error as AJAXError,
		clearLanePerformanceError: clearError,
		clearLanePerformanceData: clearData,
		callGetShipperLanePerformance: callAPI as (props?: APICallProps) => Promise<{
			data: TShipperLanePerformanceSummary;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperCarbonLanes = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/carbon/lanes', headers });

	return {
		loadingCarbonLanes: isLoading,
		carbonLanes: data as TCarbonLanesSummary,
		getCarbonLanesError: error as AJAXError,
		clearCarbonLanesError: clearError,
		clearCarbonLanesData: clearData,
		callGetShipperCarbonLanes: callAPI as (props?: APICallProps) => Promise<{
			data: TCarbonLanesSummary;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperCarbonTrend = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/carbon/trend', headers });

	return {
		loadingCarbonTrend: isLoading,
		carbonTrend: data as TCarbonTrendSummary,
		getCarbonTrendError: error as AJAXError,
		clearCarbonTrendError: clearError,
		clearCarbonTrendData: clearData,
		callGetShipperCarbonTrend: callAPI as (props?: APICallProps) => Promise<{
			data: TCarbonTrendSummary;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperPodPerformance = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/pod/performance-summary', headers });

	return {
		loadingPodPerformance: isLoading,
		podPerformance: data as TPodPerformanceSummary,
		getPodPerformanceError: error as AJAXError,
		clearPodPerformanceError: clearError,
		clearPodPerformanceData: clearData,
		callGetShipperPodPerformance: callAPI as (props?: APICallProps) => Promise<{
			data: TPodPerformanceSummary;
			error: AJAXError | null
		}>
	}
}

export const useGetAvailableTeamsForDataExtract = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/analytics/data-extract/teams',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingShipperTeam: isLoading,
		getCompanyTeamError: error as AJAXError,
		shipperTeamDetails: data,
		callGetAvailableTeamsForDataExtract: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TAdminAvailableTeamResponse } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetBatches = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/data-extract/batches', headers });

	return {
		loadingBatches: isLoading,
		batches: data as { rows: TBulkAnalyticsResponse[], total: number },
		getBatchesError: error as AJAXError,
		clearBatchesError: clearError,
		clearBatchesData: clearData,
		callGetBatches: callAPI as (props?: APICallProps) => Promise<{
			data: {
				rows: TBulkAnalyticsResponse[],
				total: number
			};
			error: AJAXError | null
		}>
	}
}

export const useDeleteBatch = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/shipper/analytics/data-extract/batches/{1}', headers });

	return {
		loadingDeleteBatch: isLoading,
		deleteBatch: data as null,
		deleteBatchError: error as AJAXError,
		clearDeleteBatchError: clearError,
		clearDeleteBatchData: clearData,
		callDeleteBatch: callAPI as (props?: APICallProps) => Promise<{
			data: null;
			error: AJAXError | null
		}>
	}
}

export const useGetTasksHistory = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/data-extract/tasks/histories', headers });

	return {
		loadingTasksHistory: isLoading,
		tasksHistory: data as TDataExtractTaskHistories,
		getTasksHistoryError: error as AJAXError,
		clearTasksHistoryError: clearError,
		clearTasksHistoryData: clearData,
		callGetTasksHistory: callAPI as (props?: APICallProps) => Promise<{
			data: TDataExtractTaskHistories;
			error: AJAXError | null
		}>
	}
}

export const useCreateBatch = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/shipper/analytics/data-extract/batches', headers });

	return {
		loadingCreateBatch: isLoading,
		createBatch: data as TBulkAnalyticsResponse,
		createBatchError: error as AJAXError,
		clearCreateBatchError: clearError,
		clearCreateBatchData: clearData,
		callCreateBatch: callAPI as (props?: APICallProps) => Promise<{
			data: TBulkAnalyticsResponse;
			error: AJAXError | null
		}>
	}
}

export const useGetDownloadBatchReport = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		data,
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/data-extract/batches/{1}/download', headers });

	return {
		batchReportData: data as Blob | null,
		batchReportDownloading: isLoading,
		callDownloadBatchReport: callAPI as (props?: APICallProps) => Promise<{
			data: Blob | null;
			error: AJAXError | null
		}>
	}
}

export const useGetBatch = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/data-extract/batches/{1}', headers });

	return {
		loadingBatch: isLoading,
		batch: data as TBulkAnalyticsResponse,
		getBatchError: error as AJAXError,
		clearBatchError: clearError,
		clearBatchData: clearData,
		callGetBatch: callAPI as (props?: APICallProps) => Promise<{
			data: TBulkAnalyticsResponse;
			error: AJAXError | null
		}>
	}
}

export const useUpdateBatch = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };
	const { isLoading, error, clearData, clearError, callAPI } = usePatchAPI({
		endpoint: '/api/shipper/analytics/data-extract/batches/{1}',
		headers,
	});
	return {
		loadingUpdateBatch: isLoading,
		updateBatchError: error as AJAXError,
		clearUpdateBatchError: clearError,
		clearUpdateBatchData: clearData,
		callUpdateBatch: callAPI as (props?: APICallProps) => Promise<{ data: TBulkAnalyticsResponse; error: AJAXError | null }>,
	};
};

export const useGetShipperDataExtracts = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/data-extract-list/{1}', headers });

	return {
		loadingDataExtracts: isLoading,
		dataExtracts: data as TShipperDataExtract[],
		getDataExtractsError: error as AJAXError,
		clearDataExtractsError: clearError,
		clearDataExtractsData: clearData,
		callGetDataExtracts: callAPI as (props?: APICallProps) => Promise<{
			data: TShipperDataExtract[];
			error: AJAXError | null
		}>
	}
}

export const useRemoveDataExtract = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/shipper/analytics/data-extract-delete', headers });

	return {
		loadingRemoveDataExtract: isLoading,
		removeDataExtract: data as any,
		removeDataExtractError: error as AJAXError,
		clearRemoveDataExtractError: clearError,
		clearRemoveDataExtractData: clearData,
		callRemoveDataExtract: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const usePrepDownloadDataExtract = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/shipper/analytics/data-extract', headers });

	return {
		loadingPrepDataExtracts: isLoading,
		prepDataExtracts: data as TShipperPrepDataExtract,
		getPrepDataExtractsError: error as AJAXError,
		clearPrepDataExtractsError: clearError,
		clearPrepDataExtractsData: clearData,
		callPrepDataExtracts: callAPI as (props?: APICallProps) => Promise<{
			data: TShipperPrepDataExtract;
			error: AJAXError | null
		}>
	}
}

export const useDownloadShipperReport = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/analytics/download-report/{1}/{2}', headers });

	return {
		loadingShipperReport: isLoading,
		shipperReport: data as any,
		getShipperReportError: error as AJAXError,
		clearShipperReportError: clearError,
		clearShipperReportData: clearData,
		callDownloadShipperReport: callAPI as (props?: APICallProps) => Promise<{
			data: any | null;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperSettings = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/shipper-settings', headers });

	return {
		loadingShipperSettings: isLoading,
		shipperSettings: data as TShipperSettings,
		getShipperSettingsError: error,
		clearGetShipperSettings: clearData,
		clearGetShipperSettingsError: clearError,
		callGetShipperSettings: callAPI as (props?: APICallProps) => Promise<{
			data: TShipperSettings;
			error: AJAXError | null;
		}>
	}
}

export const useUpdateShipperSettings = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/shipper-settings' });

	return {
		loadingUpdateShipperSettings: isLoading,
		updateShipperSettings: data as TShipperSettings,
		updateShipperSettingsError: error,
		clearUpdateShipperSettings: clearData,
		clearUpdateShipperSettingsError: clearError,
		callUpdateShipperSettings: callAPI as (props?: APICallProps) => Promise<{
			data: TShipperSettings;
			error: AJAXError | null;
		}>
	}
}

export const useGetShipperCompanyDetails = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/configuration/company',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});
	return {
		loadingShipperCompanyDetails: isLoading,
		getCompanyDetailError: error as AJAXError,
		shipperCompanyDetails: data,
		callGetShipperCompanyDetails: callAPI as (props?: APICallProps) => Promise<{
			data: { details: TAdminCompanyDetails } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperTeam = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/configuration/team',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingShipperTeam: isLoading,
		getCompanyTeamError: error as AJAXError,
		shipperTeamDetails: data,
		callGetShipperTeam: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TAdminTeamResponse } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperCostCentres = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/configuration/costcentre',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingShipperCostCentres: isLoading,
		getCompanyCostCentresError: error as AJAXError,
		shipperCostCentreDetails: data,
		callGetShipperCostCentres: callAPI as (props?: APICallProps) => Promise<{
			data: TCostCentres | null;
			error: AJAXError | null
		}>
	}
}

export const useSaveShipperCostCentre = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper/configuration/costcentresave',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingSavingShipperCostCentre: isLoading,
		getSaveCostCentresError: error as AJAXError,
		shipperSaveCostCentreDetails: data,
		callSaveShipperCostCentre: callAPI as (props?: APICallProps) => Promise<{
			data: { message: string } | null;
			error: AJAXError | null
		}>
	}
}

export const useDeleteShipperCostCentre = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/shipper/configuration/costcentre',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});
	return {
		loadingDeletingShipperCostCentre: isLoading,
		deletingCompanyCostCentreError: error as AJAXError,
		deleteShipperCostCentreDetails: data,
		callDeleteShipperCostCentre: callAPI as (props?: APICallProps) => Promise<{
			data: { message: string } | null;
			error: AJAXError | null
		}>
	}
}

export const useUpdateShipperCompanyDetails = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper/configuration/company',
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingUpdateShipperCompanyDetails: isLoading,
		updateShipperCompanyDetails: data as { type: string; message: string },
		updateShipperCompanyDetailsError: error as AJAXError,
		callUpdateShipperCompanyDetails: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperCreditCheck = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `/api/shipper/configuration/credit-check/${authUser?.company?.id}`,
		headers: { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` },
	});

	return {
		loadingShipperCreditCheck: isLoading,
		shipperCreditCheck: data,
		shipperCreditCheckError: error as AJAXError,
		callGetShipperCreditCheck: callAPI as (props?: APICallProps) => Promise<{
			data: {
				type: string,
				message: string,
				credit_score: string,
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperBillingSettings = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/configuration/billing-settings', headers });

	return {
		loadingShipperBillingSettings: isLoading,
		callGetShipperBillingSettings: callAPI as (props?: APICallProps) => Promise<{
			data: TShipperBillingSettings;
			error: AJAXError | null;
		}>
	}
}

export const useUpdateShipperBillingSettings = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		callAPI
	} = usePatchAPI({ endpoint: '/api/shipper/configuration/billing-settings', headers });

	return {
		loadingUpdateShipperSettings: isLoading,
		callUpdateShipperBillingSettings: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetShipperLicences = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/configuration/licenses-declarations', headers });

	return {
		loadingCallShipperLicences: isLoading,
		callGetShipperLicences: callAPI as (props?: APICallProps) => Promise<{
			data: TLicence[] | null;
			error: AJAXError | null;
		}>
	}

}

export const useGetCostCentreOptions = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/cost-center-options' });

	return {
		loadingCostCentreOpts: isLoading,
		costCentreOpts: data as { data: { label: string; value: number }[] },
		getCostCentreOptsError: error,
		clearCostCentreOpts: clearData,
		clearCostCentreOptsError: clearError,
		callGetCostCentreOpts: callAPI as (props?: APICallProps) => Promise<{
			data: { data: { label: string; value: number }[] };
			error: AJAXError | null;
		}>
	}
}

export const usePostFullShipment = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePostAPI({
		endpoint: '/api/full-shipment',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingPostFullShipment: isLoading,
		postFullShipment: data?.data as TShipmentEOS,
		postFullShipmentError: error as AJAXError,
		clearPostFullShipmentError: clearError,
		clearPostFullShipmentData: clearData,
		callPostFullShipment: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TShipmentEOS | null
			},
			error: AJAXError | null
		}>
	}
}

export const useGetShipperNotes = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID && sID > 0 ? sID : authUser?.shipper?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/shipnotes',
		headers: shipperToken ? { 'X-Shipper-Token': `${shipperToken}` } : undefined,
	});

	return {
		loadingNotes: isLoading,
		notes: data as TShipmentNote[],
		notesError: error,
		callGetShipperNotes: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TShipmentNote[],
				links: Link[],
				meta: {
					total: number,
					per_page: number,
				}
			},
			error: AJAXError | null
		}>
	}
}

export const useGetShipperNotesCategories = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID && sID > 0 ? sID : authUser?.shipper?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/shipnotes/note-categories',
		headers: shipperToken ? { 'X-Shipper-Token': `${shipperToken}` } : undefined,
	});

	return {
		loadingNotesCategories: isLoading,
		notesCategories: data as { data: { note_categories: TShipmentNoteCategory[], note_sub_categories: TShipmentNoteSubCategory[] } },
		notesCategoriesError: error,
		callGetShipperNotesCategories: callAPI as (props?: APICallProps) => Promise<{
			data: {
				note_categories: TShipmentNoteCategory[],
				note_sub_categories: TShipmentNoteSubCategory[]
			},
			error: AJAXError | null
		}>
	}
}

export const useGetShipperRecentNotes = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const shipperToken = sID && sID > 0 ? sID : authUser?.shipper?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/shipnotes/shipper-recent-notes',
		headers: shipperToken ? { 'X-Shipper-Token': `${shipperToken}` } : undefined,
	});

	return {
		loadingRecentNotes: isLoading,
		notes: data as TShipmentNote[],
		notesError: error,
		callGetShipperRecentNotes: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TShipmentNote[],
				links: Link[],
				meta: {
					total: number
				}
			},
			error: AJAXError | null
		}>
	}
}

export const useGetBulkUploads = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/bulk-upload',
		headers: headers,
		domain: 'ShipmentAPI'
	});

	return {
		loadingBulkUploads: isLoading,
		bulkUploads: data as { data: TBulkUploadItem[], meta: TMeta },
		getBulkUploadsError: error as AJAXError,
		clearBulkUploadsError: clearError,
		callGetBulkUploads: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TBulkUploadItem[], meta: TMeta };
			error: AJAXError | null
		}>
	}
}

export const useGetBulkUpload = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/bulk-upload/{1}',
		headers: headers,
		domain: 'ShipmentAPI'
	});

	return {
		loadingBulkUpload: isLoading,
		bulkUpload: data as { data: TBulkUploadItem },
		getBulkUploadError: error as AJAXError,
		clearBulkUploadError: clearError,
		callGetBulkUpload: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TBulkUploadItem };
			error: AJAXError | null
		}>
	}
}

export const useGetBulkUploadTemplate = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const id = sID ? sID : authUser?.shipper?.id;
	const headers = { 'X-Shipper-Token': `${id ?? undefined}` };

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/download-template',
		headers: headers,
	});

	return {
		loadingBulkUploadTemplate: isLoading,
		bulkUploadTemplate: data as { link: string },
		getBulkUploadTemplateError: error as AJAXError,
		clearBulkUploadTemplateError: clearError,
		callGetBulkUploadTemplate: callAPI as (props?: APICallProps) => Promise<{
			data: { link: string };
			error: AJAXError | null
		}>
	}
}

export const useUploadBulkUploads = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({
		endpoint: '/api/bulk-upload',
		headers: headers,
		domain: 'ShipmentAPI'
	});

	return {
		loadingUploadBulkUploads: isLoading,
		uploadBulkUploads: data as { data: { data: TBulkUploadItem } },
		getUploadBulkUploadsError: error as AJAXError,
		clearUploadBulkUploadsError: clearError,
		callUploadBulkUploads: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const useFinalizeMultiShipment = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;

	const {
		isLoading,
		callAPI
	} = usePostAPI({
		endpoint: '/api/master-shipment',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingFinalizeMultiShipment: isLoading,
		callFinalizeMultiShipment: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: {
					external_id: string;
					shipments: TShipmentEOS[];
				}
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useRemoveShipmentEosDoc = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;

	const {
		isLoading,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/documents/{1}',
		headers
	});

	return {
		loadingRemoveEosDoc: isLoading,
		callRemoveEosDoc: callAPI as (props?: APICallProps) => Promise<{
			data: { success: boolean } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetMasterShipment = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;
	const {
		isLoading,
		callAPI
	} = useGetAPI({
		endpoint: '/api/master-shipment/{1}',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingMasterShipment: isLoading,
		callGetShipperMasterShipment: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TMasterShipmentMultiShipment
			},
			error: AJAXError | null
		}>
	}
}

export const useGetSuperdockFromShipment = () => {
	const shipperID = useRecoilValue(XCustomerID);
	const headers = shipperID ? { 'X-Customer-Id': `${shipperID}` } : undefined;
	const {
		isLoading,
		callAPI
	} = useGetAPI({
		endpoint: '/api/v2/super-dock/contains/{1}',
		headers
	});

	return {
		loadingSDFromShipment: isLoading,
		callGetSDFromShipment: callAPI as (props?: APICallProps) => Promise<{
			data: {
				status: 'success' | 'pending';
				super_dock_id: number | null;
			},
			error: AJAXError | null
		}>
	}
}

export const useUpdateShipperLocationDetails = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePatchAPI({
		endpoint: '/api/shipper/locations/{1}/shipper-details',
		headers: headers,
	});

	const updateShipperLocationDetails = async (locationId: string, payload: any) => {
		await callAPI({ routes: [locationId], payload });
	};

	return {
		loadingUpdateShipperLocationDetails: isLoading,
		updateShipperLocationDetailsData: data,
		updateShipperLocationDetailsError: error as AJAXError,
		clearUpdateShipperLocationDetailsError: clearError,
		updateShipperLocationDetails
	}
}

export const useGetLocationDocuments = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/locations/{1}/documents',
		headers
	});

	const getLocationDocuments = async (locationId: string) => {
		await callAPI({ routes: [locationId] });
	};

	return {
		loadingLocationDocuments: isLoading,
		locationDocumentsData: data,
		locationDocumentsError: error as AJAXError,
		clearLocationDocumentsError: clearError,
		getLocationDocuments
	}
}

export const useLinkLocationDocument = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper/locations/{1}/documents',
		headers
	});

	const linkLocationDocument = async (locationId: string, docId: string) => {
		await callAPI({ routes: [locationId], payload: { doc_id: docId } });
	};

	return {
		loadingLinkLocationDocument: isLoading,
		linkLocationDocumentData: data,
		linkLocationDocumentError: error as AJAXError,
		clearLinkLocationDocumentError: clearError,
		linkLocationDocument
	}
}

export const useDeleteLocationDocument = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/shipper/locations/{1}/documents/{2}',
		headers
	});

	const deleteLocationDocument = async (locationId: string, docId: string) => {
		await callAPI({ routes: [locationId, docId] });
	};

	return {
		loadingDeleteLocationDocument: isLoading,
		deleteLocationDocumentData: data,
		deleteLocationDocumentError: error as AJAXError,
		clearDeleteLocationDocumentError: clearError,
		deleteLocationDocument
	}
}

/* SHIPPER -> DOCUMENT HUB */
export const useGetCompanyDocuments = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/company/documents', headers });

	return {
		loadingCompanyDocuments: isLoading,
		callGetCompanyDocuments: callAPI as (props?: APICallProps) => Promise<{
			data: TCompanyDocument[] | null;
			error: AJAXError | null;
		}>
	}
}

export const useUploadCompanyDocument = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/shipper/company/documents', headers });

	return {
		loadingUploadDocument: isLoading,
		callUploadCompanyDocument: callAPI as (props?: APICallProps) => Promise<{
			data: { message: string } | null;
			error: AJAXError | null;
		}>
	}
}

export const useDownloadCompanyDocument = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/company/documents/{1}/download', headers });

	return {
		loadingDownloadDocument: isLoading,
		callDownloadCompanyDocument: callAPI as (props?: APICallProps) => Promise<{
			data: Blob | null;
			error: AJAXError | null;
		}>
	}
}

export const useDownloadShipperTransportDocument = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/transport/documents/{1}/download', headers });

	return {
		loadingTransportDocument: isLoading,
		callDownloadTransportDocument: callAPI
	};
}

export const useDeleteCompanyDocument = () => {
	const sID = useRecoilValue(SID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Shipper-Token': `${sID ?? authUser?.shipper?.id}` };

	const {
		isLoading,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/shipper/company/documents/{1}', headers });

	return {
		loadingDeleteCompanyDocument: isLoading,
		callDeleteCompanyDocument: callAPI as (props?: APICallProps) => Promise<{
			data: { message: string } | null;
			error: AJAXError | null;
		}>
	}
}
